/**
 * SearchConsoleTopPages.jsx — Phase 84.36
 *
 * Marketing Overview widget: shows the top web pages by clicks in the last
 * 30 days from the connected user's Google Search Console property. If the
 * user isn't Google-connected yet (or the token predates the addition of
 * `webmasters.readonly` — impossible in practice since it's been in
 * SHEETS_SCOPES since day one, but we still handle it), we render a "Connect
 * Google" nudge that hands off to the shared OAuth flow.
 *
 * The Marketing project's live-strip already surfaces YouTube; this widget
 * complements that with web-side analytics — turning the Overview into a
 * single-pane-of-glass across socials + owned surfaces.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Globe2, ExternalLink, TrendingUp, Loader2 } from "lucide-react";

export default function SearchConsoleTopPages() {
  const [state, setState] = useState({ loading: true, connected: false, items: [], sites: [], site: null });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Ask for top-pages directly — the endpoint auto-picks the first
        // verified site so we don't need a separate sites call for the
        // happy path.
        const { data } = await api.get("/integrations/search-console/top-pages?limit=8&days=30");
        if (!live) return;
        setState({
          loading: false,
          connected: Boolean(data?.site),
          items: data?.items || [],
          site: data?.site || null,
          sites: data?.sites || [],
        });
      } catch {
        if (live) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { live = false; };
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.post("/integrations/google/connect");
      if (data?.auth_url) window.location.href = data.auth_url;
      else toast.error("Could not start Google OAuth");
    } catch { toast.error("Could not start Google OAuth"); }
  };

  return (
    <div className="card-flat p-5" data-testid="sc-top-pages">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="label-overline">Website · last 30 days</div>
          <div className="font-display text-lg font-bold text-slate-900 mt-0.5 flex items-center gap-2">
            Top pages by clicks
            {state.connected && (
              <span className="text-[8px] px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-bold">LIVE</span>
            )}
          </div>
          {state.site && (
            <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-[280px]" title={state.site}>
              via Search Console · {state.site.replace("sc-domain:", "").replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </div>
          )}
        </div>
        <Globe2 size={14} className="text-[#F54800] shrink-0" />
      </div>

      {state.loading ? (
        <div className="flex items-center justify-center py-8 text-slate-400 text-xs" data-testid="sc-loading">
          <Loader2 size={14} className="animate-spin mr-1.5" /> Loading…
        </div>
      ) : !state.connected ? (
        <div className="text-center py-6" data-testid="sc-empty">
          <Globe2 size={24} className="text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-600 mb-3 max-w-xs mx-auto">
            Connect your Google account to see your top web pages by clicks, impressions and CTR.
          </p>
          <button
            onClick={connect}
            className="text-xs bg-[#F54800] hover:bg-[#D63E00] text-white px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5"
            data-testid="sc-connect-btn"
          >
            <ExternalLink size={11} /> Connect Google
          </button>
          <p className="text-[9px] text-slate-400 mt-2">Requires a verified Search Console property on the connected account.</p>
        </div>
      ) : state.items.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400" data-testid="sc-no-data">
          No page-click data yet for this property.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="sc-items">
          {state.items.map((row, i) => (
            <li
              key={row.id}
              className="flex items-center gap-3 group border border-transparent hover:border-slate-200 rounded-sm px-2 py-1.5 transition-colors"
              data-testid={`sc-item-${i}`}
            >
              <span className="text-[10px] font-mono text-slate-400 w-4 text-right shrink-0">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-900 truncate">{row.text}</div>
                <a
                  href={row.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] text-slate-500 hover:text-[#F54800] truncate inline-flex items-center gap-0.5"
                  title={row.permalink}
                >
                  <span className="truncate max-w-[280px]">{row.permalink}</span>
                  <ExternalLink size={9} className="opacity-0 group-hover:opacity-100 shrink-0" />
                </a>
              </div>
              <div className="text-right shrink-0 flex items-center gap-3">
                <div>
                  <div className="text-xs font-bold text-slate-900 leading-none" data-testid={`sc-item-${i}-clicks`}>
                    {(row.engagement || row.likes || 0).toLocaleString()}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">clicks</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-700 leading-none">
                    {(row.impressions || 0).toLocaleString()}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">imps</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-700 leading-none inline-flex items-center gap-0.5">
                    <TrendingUp size={9} className="text-emerald-600" />
                    {row.ctr ? (row.ctr * 100).toFixed(1) : "0.0"}%
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">CTR</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-700 leading-none">
                    #{row.avg_position ? row.avg_position.toFixed(1) : "—"}
                  </div>
                  <div className="text-[9px] text-slate-400 mt-0.5">avg pos</div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
